/**
 * The single definition of where the backend lives.
 *
 * `VITE_API_BASE_URL` is baked into the bundle at build time, so a wrong value is
 * not a misconfiguration an operator can correct on the running box -- it is a
 * broken image that has to be rebuilt. That makes the "unset" case worth being
 * exact about.
 *
 * This module exists because it was not exact. Twelve modules each wrote
 * `import.meta.env.VITE_API_BASE_URL ?? '/api'`, and `??` tests for nullish, not
 * empty. CI passed `VITE_API_BASE_URL=${{ secrets.STAGING_API_URL }}` with that
 * secret unset; GitHub expanded it to the empty string; the fallback did not fire;
 * and every published Demo bundle requested `/auth/login` instead of
 * `/api/auth/login`. A green build shipped a frontend that 404s on every call, and
 * nothing anywhere reported a problem.
 *
 * So: one resolver, one definition of unset (nullish **or** blank), one default.
 */

/**
 * Default base path. Correct for every containerised deployment: nginx serves the
 * SPA and reverse-proxies `/api/` to the backend from the same origin
 * (`docker/nginx.conf`), so a relative path needs no per-environment value at all.
 */
const DEFAULT_API_BASE_URL = '/api'

/**
 * Resolve the API base URL from a raw environment value.
 *
 * Exported for testing; application code should import {@link API_BASE_URL}.
 *
 * @param raw - The build-time environment value, if any.
 * @returns The base URL to prefix onto request paths, without a trailing slash.
 */
export function resolveApiBaseUrl(raw: string | undefined): string {
  const trimmed = raw?.trim()

  if (trimmed === undefined || trimmed === '') {
    return DEFAULT_API_BASE_URL
  }

  // A trailing slash would produce `//api/...` once a path is appended. Harmless on
  // most servers, but it breaks exact-match route comparisons and looks like a bug
  // in every network log a colleague reads during a demo.
  return trimmed.replace(/\/+$/, '')
}

/**
 * Base URL for all backend requests. Prefix onto paths that already start with `/`.
 */
export const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)
